import { useQuery } from "@tanstack/react-query"
import { Cake, Copy, Download, ImageUp, Loader2, UserRound } from "lucide-react"
import { QRCodeCanvas } from "qrcode.react"
import { useEffect, useRef, useState } from "react"

import { api, pedirBinario } from "@/api/cliente"
import { urlDeLaWeb } from "@/componentes/carnet/contenido"
import { comoSeLlama, type Empresa } from "@/componentes/empresa/tipos"
import { LogoSetieca } from "@/componentes/marca/LogoSetieca"
import { Boton } from "@/componentes/ui/Boton"
import { BotonArchivo } from "@/componentes/ui/BotonArchivo"
import { EstadoCarga, EstadoError } from "@/componentes/ui/Estados"
import { comoFecha } from "@/lib/fechas"

import type { FotoLista } from "./recorte"
import { ALTO, ANCHO, dibujarTarjeta, type Encuadre, fuentesListas, leerPaleta } from "./tarjeta"

type Cumpleanero = {
  persona_id: number
  nombres: string
  apellidos: string
  cargo: string | null
  fecha: string
  dias: number
  tiene_foto: boolean
}

const MESES = [
  "ENERO",
  "FEBRERO",
  "MARZO",
  "ABRIL",
  "MAYO",
  "JUNIO",
  "JULIO",
  "AGOSTO",
  "SEPTIEMBRE",
  "OCTUBRE",
  "NOVIEMBRE",
  "DICIEMBRE",
]

/** «09 DE OCTUBRE, 2026», como la tarjeta de referencia. */
export function fechaLarga(iso: string): string {
  const [a, m, d] = iso.split("-")
  return `${d} DE ${MESES[Number(m) - 1] ?? ""}, ${a}`
}

/** Primer nombre y primer apellido: «Luis Alvarez», no el nombre completo de la cédula. */
export function nombreCorto(c: Pick<Cumpleanero, "nombres" | "apellidos">): string {
  return [c.nombres.split(/\s+/)[0], c.apellidos.split(/\s+/)[0]].filter(Boolean).join(" ")
}

const MENSAJE =
  "La familia SETIECA te desea un día maravilloso lleno de alegría, y un año de éxitos continuos."
const ENCUADRE: Encuadre = { zoom: 1, dx: 0, dy: 0 }

function cuando(dias: number): string {
  if (dias === 0) return "¡hoy!"
  if (dias === 1) return "mañana"
  return `en ${dias} días`
}

/** El «25» de la marca, rasterizado en el vino del papel para dibujarlo en el lienzo. */
async function logoComoImagen(svg: SVGSVGElement | null, color: string) {
  if (!svg) return null
  try {
    const copia = svg.cloneNode(true) as SVGSVGElement
    copia.setAttribute("style", `color: ${color}`)
    const caja = svg.viewBox.baseVal
    copia.setAttribute("width", "600")
    copia.setAttribute("height", String(Math.round((600 * caja.height) / caja.width)))
    const imagen = new Image()
    imagen.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(new XMLSerializer().serializeToString(copia))}`
    await imagen.decode()
    return imagen
  } catch {
    return null
  }
}

/**
 * Cumpleaños del personal y su tarjeta (24/10; `services/cumpleanos.py`). Se elige a
 * quien cumple —sale su foto de la ficha— o se sube otra; el NAVEGADOR le quita el fondo
 * y la encuadra en primer plano (`recorte.ts`), y la tarjeta se descarga en PNG para
 * mandarla por WhatsApp o Telegram.
 *
 * ⚠ **La foto no sale del sistema**: se recorta aquí mismo, con MediaPipe servido desde
 * nuestro origen. El encuadre automático se puede corregir (acercar y mover).
 */
export function Cumpleanos() {
  const proximos = useQuery({
    queryKey: ["cumpleanos"],
    queryFn: () => api.get<Cumpleanero[]>("/api/v1/cumpleanos?dias=90"),
  })
  const empresa = useQuery({
    queryKey: ["empresa"],
    queryFn: () => api.get<Empresa>("/api/v1/empresa"),
  })
  const [elegido, setElegido] = useState<Cumpleanero | null>(null)
  const [foto, setFoto] = useState<FotoLista | null>(null)
  const [fuente, setFuente] = useState<Blob | null>(null)
  const [procesando, setProcesando] = useState(false)
  const [fallo, setFallo] = useState<string | null>(null)
  const [quitarFondo, setQuitarFondo] = useState(true)
  const [encuadre, setEncuadre] = useState<Encuadre>(ENCUADRE)
  const [nombre, setNombre] = useState("")
  const [mensaje, setMensaje] = useState(MENSAJE)
  const [fecha, setFecha] = useState("")
  const [copiado, setCopiado] = useState(false)
  const [logo, setLogo] = useState<HTMLImageElement | null>(null)
  const lienzo = useRef<HTMLCanvasElement>(null)
  const marca = useRef<HTMLDivElement>(null)
  const qr = useRef<HTMLCanvasElement>(null)
  const paleta = useRef<ReturnType<typeof leerPaleta> | null>(null)

  const url = urlDeLaWeb(empresa.data)
  const agencia = empresa.data ? comoSeLlama(empresa.data) : ""

  useEffect(() => {
    paleta.current ??= leerPaleta()
    logoComoImagen(marca.current?.querySelector("svg") ?? null, paleta.current.vinoOscuro).then(
      setLogo,
    )
  }, [])

  async function procesar(blob: Blob, sinFondo: boolean) {
    setProcesando(true)
    setFallo(null)
    try {
      const { prepararFoto } = await import("./recorte")
      setFoto(await prepararFoto(blob, sinFondo))
      setEncuadre(ENCUADRE)
    } catch {
      setFallo("No se pudo preparar la foto. Prueba con otra (JPG o PNG).")
      setFoto(null)
    } finally {
      setProcesando(false)
    }
  }

  async function elegir(c: Cumpleanero) {
    setElegido(c)
    setNombre(nombreCorto(c))
    setFecha(fechaLarga(c.fecha))
    setFoto(null)
    setFuente(null)
    if (!c.tiene_foto) return
    try {
      const blob = await pedirBinario(`/api/v1/cumpleanos/${c.persona_id}/foto`)
      setFuente(blob)
      await procesar(blob, quitarFondo)
    } catch {
      setFallo("No se pudo traer la foto de su ficha: súbela aquí.")
    }
  }

  // El lienzo es la tarjeta: se redibuja con cada cambio.
  useEffect(() => {
    let vivo = true
    fuentesListas().then(() => {
      const c = lienzo.current
      if (!vivo || !c) return
      paleta.current ??= leerPaleta()
      dibujarTarjeta(
        c,
        {
          foto: foto?.imagen ?? null,
          cara: foto?.cara ?? null,
          encuadre,
          nombre,
          mensaje,
          fecha,
          agencia,
          logo,
          qr: url ? qr.current : null,
        },
        paleta.current,
      )
    })
    return () => {
      vivo = false
    }
  }, [foto, encuadre, nombre, mensaje, fecha, agencia, logo, url])

  async function comoPng(): Promise<Blob | null> {
    const c = lienzo.current
    return c ? await new Promise((ok) => c.toBlob(ok, "image/png")) : null
  }

  async function descargar() {
    const blob = await comoPng()
    if (!blob) return
    const enlace = document.createElement("a")
    enlace.href = URL.createObjectURL(blob)
    enlace.download = `Cumpleaños ${nombre || "SETIECA"}.png`
    enlace.click()
    setTimeout(() => URL.revokeObjectURL(enlace.href), 1000)
  }

  async function copiar() {
    const blob = await comoPng()
    if (!blob) return
    try {
      await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })])
      setCopiado(true)
      setTimeout(() => setCopiado(false), 2000)
    } catch {
      setFallo("El navegador no dejó copiar la imagen: descárgala.")
    }
  }

  const deslizador = (
    rotulo: string,
    clave: keyof Encuadre,
    min: number,
    max: number,
    paso: number,
  ) => (
    <label className="block text-sm">
      {rotulo}
      <input
        type="range"
        className="mt-1 w-full"
        min={min}
        max={max}
        step={paso}
        value={encuadre[clave]}
        onChange={(e) => setEncuadre({ ...encuadre, [clave]: Number(e.target.value) })}
      />
    </label>
  )

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,18rem)_minmax(0,1fr)]">
      {/* Fuera de la vista: lo que el lienzo copia (el «25» y el QR). */}
      <div aria-hidden="true" className="pointer-events-none fixed -left-[9999px] top-0">
        <div ref={marca}>
          <LogoSetieca pieza="numero" />
        </div>
        {url && (
          <QRCodeCanvas
            ref={qr}
            value={url}
            size={300}
            level="M"
            marginSize={0}
            fgColor={paleta.current?.tinta ?? "black"}
            bgColor={paleta.current?.papel ?? "white"}
          />
        )}
      </div>

      <section aria-label="Próximos cumpleaños" className="space-y-2">
        <h2 className="flex items-center gap-2 text-base font-semibold">
          <Cake size={18} aria-hidden={true} /> Próximos cumpleaños
        </h2>
        {proximos.isPending && <EstadoCarga filas={4} />}
        {proximos.isError && <EstadoError titulo="No se pudo leer" error={proximos.error} />}
        {proximos.data?.length === 0 && (
          <p className="tarjeta p-4 text-sm" style={{ color: "var(--fg-muted)" }}>
            Nadie cumple en los próximos 90 días, o falta la fecha de nacimiento en las fichas.
          </p>
        )}
        <ul className="tarjeta max-h-[32rem] divide-y overflow-y-auto">
          {(proximos.data ?? []).map((c) => (
            <li key={c.persona_id}>
              <button
                type="button"
                aria-current={elegido?.persona_id === c.persona_id ? "true" : undefined}
                onClick={() => elegir(c)}
                className={`foco-cristal w-full p-3 text-left text-sm ${elegido?.persona_id === c.persona_id ? "activo-cristal" : "toque-cristal"}`}
              >
                <span className="block font-medium">
                  {c.nombres} {c.apellidos}
                </span>
                <span className="block text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
                  {comoFecha(c.fecha)} · {cuando(c.dias)}
                  {c.cargo ? ` · ${c.cargo}` : ""}
                  {c.tiene_foto ? "" : " · sin foto en la ficha"}
                </span>
              </button>
            </li>
          ))}
        </ul>
        <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
          Sale de la fecha de nacimiento de cada ficha de Personas.
        </p>
      </section>

      <section aria-label="Tarjeta de cumpleaños" className="space-y-4">
        {!elegido && (
          <p className="tarjeta flex items-center gap-2 p-4 text-sm">
            <UserRound size={18} aria-hidden={true} />
            Elige a quien cumple, o sube una foto, y la tarjeta se arma sola.
          </p>
        )}
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,30rem)_minmax(0,1fr)]">
          <div className="relative">
            <canvas
              ref={lienzo}
              width={ANCHO}
              height={ALTO}
              aria-label={`Tarjeta de cumpleaños de ${nombre || "…"}`}
              className="w-full rounded-[var(--radius-control)] shadow"
              role="img"
            />
            {procesando && (
              <div
                role="status"
                className="absolute inset-0 grid place-items-center rounded-[var(--radius-control)] text-sm"
                style={{ background: "rgb(0 0 0 / 0.45)", color: "var(--primary-fg)" }}
              >
                <span className="flex items-center gap-2">
                  <Loader2 size={18} className="animate-spin" aria-hidden={true} />
                  Limpiando el fondo y encuadrando…
                </span>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <BotonArchivo
                accept="image/png,image/jpeg,image/webp"
                etiqueta="Subir otra foto"
                onArchivo={(archivo) => {
                  setFuente(archivo)
                  if (!elegido) setFecha(fechaLarga(new Date().toISOString().slice(0, 10)))
                  procesar(archivo, quitarFondo)
                }}
              >
                <ImageUp size={14} aria-hidden={true} /> Subir otra foto
              </BotonArchivo>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={quitarFondo}
                onChange={(e) => {
                  setQuitarFondo(e.target.checked)
                  if (fuente) procesar(fuente, e.target.checked)
                }}
              />
              Quitar el fondo de la foto
            </label>
            {fallo && (
              <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
                {fallo}
              </p>
            )}
            {foto && (
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium">
                  Encuadre {foto.cara ? "(primer plano automático)" : "(no se encontró la cara)"}
                </legend>
                {deslizador("Acercar", "zoom", 0.6, 2.2, 0.02)}
                {deslizador("Mover a los lados", "dx", -0.5, 0.5, 0.01)}
                {deslizador("Mover arriba y abajo", "dy", -0.5, 0.5, 0.01)}
                <Boton tamano="sm" variante="fantasma" onClick={() => setEncuadre(ENCUADRE)}>
                  Volver al encuadre automático
                </Boton>
              </fieldset>
            )}
            <label className="block text-sm">
              Nombre en la tarjeta
              <input
                className="campo mt-1"
                value={nombre}
                maxLength={40}
                onChange={(e) => setNombre(e.target.value)}
              />
            </label>
            <label className="block text-sm">
              Mensaje
              <textarea
                className="campo mt-1"
                rows={3}
                maxLength={180}
                value={mensaje}
                onChange={(e) => setMensaje(e.target.value)}
              />
            </label>
            <label className="block text-sm">
              Fecha
              <input
                className="campo mt-1"
                value={fecha}
                maxLength={40}
                onChange={(e) => setFecha(e.target.value)}
              />
            </label>
            <div className="flex flex-wrap gap-2">
              <Boton variante="primario" onClick={descargar} disabled={procesando}>
                <Download size={16} aria-hidden={true} /> Descargar PNG
              </Boton>
              <Boton onClick={copiar} disabled={procesando}>
                <Copy size={16} aria-hidden={true} /> {copiado ? "Copiada" : "Copiar imagen"}
              </Boton>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
